import Countdown, { zeroPad } from 'react-countdown';
import styled from 'styled-components';
import { ICountdownProps, ICountdownRendererProps } from 'shared/types/Countdown';

// Renderer callback with condition
const CountdownRenderer = ({
  formatted: { days, hours, minutes, seconds },
  isLowercase,
  hideDaysWhenZero,
  transitionToRed,
  className,
}: ICountdownRendererProps) => {
  const [daysNum, hoursNum] = [Number.parseInt(days, 10) || 0, Number.parseInt(hours, 10) || 0];
  const hideDays = hideDaysWhenZero && daysNum <= 0;
  const redText = transitionToRed && daysNum <= 0 && hoursNum < 24;

  return (
    <CountdownRenderer.CountdownContainer
      suppressHydrationWarning
      data-qa="countdown"
      className={className}
      $redText={redText}
      $isLowercase={isLowercase}
    >
      {`${hideDays ? '' : `${zeroPad(days)}D `}${hours}H ${minutes}M ${seconds}S`}
    </CountdownRenderer.CountdownContainer>
  );
};

/**
 * [CountdownComponent]
 * Renders a countdown in the format 00d 00h 00m 00s.
 * @param {boolean} active - Does the countdown start automatically?
 * @param {string | number | Date} startTime - How long from the current time should it countdown to?
 * @param {boolean} hideDaysWhenZero - Should the days be hidden if the `# of days` = 0?
 * @param {boolean} hoursOnly - Show days in hours?
 * @param {boolean} isLowercase - Use lower case D, H, M, S?
 * @param {boolean} transitionToRed - Should the countdown turn red during the last 24 hours?
 */
export const CountdownComponent = ({
  active = true,
  countdownRef,
  startTime,
  hoursOnly = false,
  hideDaysWhenZero = false,
  isLowercase = false,
  transitionToRed = false,
  className,
}: ICountdownProps) => {
  return (
    <Countdown
      autoStart={active}
      date={startTime}
      daysInHours={hoursOnly}
      ref={countdownRef}
      renderer={(props) => (
        <CountdownRenderer
          isLowercase={isLowercase}
          hideDaysWhenZero={hideDaysWhenZero || hoursOnly}
          transitionToRed={transitionToRed}
          className={className}
          {...props}
        />
      )}
    />
  );
};

CountdownRenderer.CountdownContainer = styled.span<{ $redText: boolean; $isLowercase: boolean }>`
  display: block;
  white-space: nowrap;
  ${({ $isLowercase }) => $isLowercase && 'text-transform: lowercase'};
  color: ${({ theme, $redText }) =>
    $redText ? theme.colors.red : theme.navigation.textColor || 'black'};
`;
